// ─────────────────────────────────────────────────────────────────────────────
// Visitor Tracker
//
// Two-layer storage:
//   1. localStorage  — device-local history, max 100 records
//   2. Supabase      — cross-device cloud storage (requires .env config)
//
// Supabase REST API is used directly (no SDK needed).
// Configure in .env.local:
//   VITE_SUPABASE_URL=https://xxxx.supabase.co
//   VITE_SUPABASE_ANON_KEY=eyJhbGci...
// ─────────────────────────────────────────────────────────────────────────────

export interface VisitorRecord {
  id: string;
  timestamp: string;
  // IP & geo
  ip: string;
  country: string;
  country_code: string;
  region: string;
  city: string;
  timezone: string;
  isp: string;
  // Browser
  browser: string;
  browser_version: string;
  os: string;
  device: 'Mobile' | 'Tablet' | 'Desktop';
  // Context
  language: string;
  referrer: string;
  screen_resolution: string;
  viewport: string;
  page_url: string;
}

const STORAGE_KEY = '__foley_visits';
const MAX_RECORDS  = 100;

// ── Supabase config (set via .env.local) ────────────────────────────────────
const SB_URL  = (import.meta as any).env?.VITE_SUPABASE_URL  as string | undefined;
const SB_KEY  = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string | undefined;
export const supabaseConfigured = !!(SB_URL && SB_KEY);

// ── Browser / OS / Device helpers ────────────────────────────────────────────

function parseBrowser(ua: string): { browser: string; version: string } {
  const tests: [RegExp, string][] = [
    [/EdgA?\/([\d.]+)/,           'Edge'],
    [/OPR\/([\d.]+)/,             'Opera'],
    [/Chrome\/([\d.]+)/,          'Chrome'],
    [/Firefox\/([\d.]+)/,         'Firefox'],
    [/Version\/([\d.]+).*Safari/, 'Safari'],
    [/Safari\/([\d.]+)/,          'Safari'],
    [/MSIE ([\d.]+)/,             'IE'],
    [/Trident.*rv:([\d.]+)/,      'IE'],
  ];
  for (const [re, name] of tests) {
    const m = ua.match(re);
    if (m) return { browser: name, version: m[1] ?? '' };
  }
  return { browser: 'Unknown', version: '' };
}

function parseOS(ua: string): string {
  if (/iPhone|iPad|iPod/.test(ua)) {
    const m = ua.match(/OS ([\d_]+)/);
    return `iOS ${m ? m[1].replace(/_/g, '.') : ''}`;
  }
  if (/Android/.test(ua)) {
    const m = ua.match(/Android ([\d.]+)/);
    return `Android ${m ? m[1] : ''}`;
  }
  if (/Windows NT ([\d.]+)/.test(ua)) {
    const ver: Record<string, string> = { '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7', '6.0': 'Vista' };
    const m = ua.match(/Windows NT ([\d.]+)/);
    return `Windows ${ver[m?.[1] ?? ''] ?? m?.[1] ?? ''}`;
  }
  if (/Mac OS X ([\d_]+)/.test(ua)) {
    const m = ua.match(/Mac OS X ([\d_]+)/);
    return `macOS ${m ? m[1].replace(/_/g, '.') : ''}`;
  }
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

function parseDevice(ua: string): 'Mobile' | 'Tablet' | 'Desktop' {
  if (/iPad|Tablet|PlayBook/i.test(ua)) return 'Tablet';
  if (/Mobi|iPhone|iPod|Android|BlackBerry|IEMobile/i.test(ua)) return 'Mobile';
  return 'Desktop';
}

// ── Geo / IP lookup ───────────────────────────────────────────────────────────

/** 带超时的 fetch（兼容所有浏览器，不依赖 AbortSignal.timeout） */
function fetchWithTimeout(url: string, ms = 8000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
}

/**
 * Step 1: 从 Cloudflare CDN Trace 获取真实 IP（极高可靠性，返回纯文本）
 * 响应示例: "fl=...\nip=1.2.3.4\n...loc=CN\n..."
 */
async function getIpAndCountryFromCloudflare(): Promise<{ ip: string; country_code: string }> {
  const res  = await fetchWithTimeout('https://cloudflare.com/cdn-cgi/trace', 5000);
  const text = await res.text();
  const ip   = text.match(/ip=([^\n]+)/)?.[1]?.trim() ?? '';
  const loc  = text.match(/loc=([^\n]+)/)?.[1]?.trim() ?? '';
  return { ip, country_code: loc };
}

/** ipwho.is — 用于获取详细地理信息（城市、省份、ISP 等） */
async function tryIpwhoIs(ip: string) {
  const url = ip ? `https://ipwho.is/${ip}` : 'https://ipwho.is/';
  const res  = await fetchWithTimeout(url);
  const d    = await res.json();
  if (d.success === false) throw new Error(`ipwho.is: ${d.message}`);
  return {
    ip:           String(d.ip           ?? ip),
    country:      String(d.country      ?? ''),
    country_code: String(d.country_code ?? ''),
    region:       String(d.region       ?? ''),
    city:         String(d.city         ?? ''),
    timezone:     String(d.timezone?.id ?? d.timezone ?? ''),
    isp:          String(d.connection?.isp ?? d.connection?.org ?? ''),
  };
}

/** freeipapi.com — 备选 */
async function tryFreeIpApi(ip: string) {
  const url = ip ? `https://freeipapi.com/api/json/${ip}` : 'https://freeipapi.com/api/json';
  const res  = await fetchWithTimeout(url);
  const d    = await res.json();
  if (!d.ipAddress) throw new Error('freeipapi: no ipAddress');
  return {
    ip:           String(d.ipAddress   ?? ip),
    country:      String(d.countryName ?? ''),
    country_code: String(d.countryCode ?? ''),
    region:       String(d.regionName  ?? ''),
    city:         String(d.cityName    ?? ''),
    timezone:     String(d.timeZones?.[0] ?? ''),
    isp:          '',
  };
}

/** ipapi.co — 第二备选（有频率限制，放最后） */
async function tryIpapiCo(ip: string) {
  const url = ip ? `https://ipapi.co/${ip}/json/` : 'https://ipapi.co/json/';
  const res  = await fetchWithTimeout(url);
  const d    = await res.json();
  if (d.error) throw new Error(`ipapi.co: ${d.reason}`);
  return {
    ip:           String(d.ip           ?? ip),
    country:      String(d.country_name ?? ''),
    country_code: String(d.country_code ?? ''),
    region:       String(d.region       ?? ''),
    city:         String(d.city         ?? ''),
    timezone:     String(d.timezone     ?? ''),
    isp:          String(d.org          ?? ''),
  };
}

async function fetchGeo() {
  // ① 先用 Cloudflare CDN Trace 获取真实 IP（极可靠）
  let knownIp = '';
  let knownCountryCode = '';
  try {
    const cf = await getIpAndCountryFromCloudflare();
    knownIp          = cf.ip;
    knownCountryCode = cf.country_code;
    console.log(`%c[Foley DevTools] Cloudflare IP: ${knownIp} (${knownCountryCode})`, 'color:#6366f1');
  } catch (e) {
    console.warn('[Foley DevTools] Cloudflare trace 失败:', e);
  }

  // ② 用已知 IP 向详细地理 API 查询（城市/省份/ISP）
  const geoApis = [tryIpwhoIs, tryFreeIpApi, tryIpapiCo];
  for (const api of geoApis) {
    try {
      const geo = await api(knownIp);
      if (geo.ip || knownIp) {
        // 若 API 未返回 IP，补上 Cloudflare 取到的
        if (!geo.ip) geo.ip = knownIp;
        if (!geo.country_code) geo.country_code = knownCountryCode;
        return geo;
      }
    } catch (e) {
      console.warn(`[Foley DevTools] ${api.name} 失败:`, e);
    }
  }

  // ③ 所有详细 API 均失败，但至少返回 Cloudflare 取到的 IP
  if (knownIp) {
    return {
      ip: knownIp, country: '', country_code: knownCountryCode,
      region: '', city: '', timezone: '', isp: '',
    };
  }

  throw new Error('All geo APIs failed');
}

// ── localStorage helpers ─────────────────────────────────────────────────────

export function loadLocalRecords(): VisitorRecord[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); }
  catch { return []; }
}

function saveLocalRecords(records: VisitorRecord[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(-MAX_RECORDS))); }
  catch { /* quota exceeded */ }
}

// ── Supabase helpers ─────────────────────────────────────────────────────────

function sbHeaders() {
  return {
    'Content-Type': 'application/json',
    'apikey':        SB_KEY!,
    'Authorization': `Bearer ${SB_KEY}`,
    'Prefer':        'return=minimal',
  };
}

async function pushToSupabase(record: VisitorRecord) {
  if (!supabaseConfigured) return;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    await fetch(`${SB_URL}/rest/v1/visitors`, {
      method:  'POST',
      headers: sbHeaders(),
      body:    JSON.stringify(record),
      signal:  controller.signal,
    }).finally(() => clearTimeout(timer));
  } catch {
    /* silent – tracking must never break the page */
  }
}

export async function fetchSupabaseVisitors(limit = 200): Promise<VisitorRecord[]> {
  if (!supabaseConfigured) return [];
  try {
    const res = await fetch(
      `${SB_URL}/rest/v1/visitors?select=*&order=timestamp.desc&limit=${limit}`,
      { headers: { apikey: SB_KEY!, Authorization: `Bearer ${SB_KEY}` } }
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  } catch {
    return [];
  }
}

export async function fetchSupabaseTotalCount(): Promise<number | null> {
  if (!supabaseConfigured) return null;
  try {
    const res = await fetch(
      `${SB_URL}/rest/v1/visitors?select=id`,
      {
        headers: {
          apikey:        SB_KEY!,
          Authorization: `Bearer ${SB_KEY}`,
          Prefer:        'count=exact',
          'Range-Unit':  'items',
          Range:         '0-0',
        },
      }
    );
    const range = res.headers.get('Content-Range'); // e.g. "0-0/42"
    const total = range?.split('/')[1];
    return total ? parseInt(total, 10) : null;
  } catch {
    return null;
  }
}

// ── CounterAPI (free global counter, no account needed) ──────────────────────

export async function fetchTotalCount(): Promise<number | null> {
  // Prefer Supabase exact count when configured
  if (supabaseConfigured) return fetchSupabaseTotalCount();
  try {
    const res = await fetch('https://api.counterapi.dev/v1/foley-demo-page/visits');
    if (!res.ok) return null;
    return (await res.json()).count ?? null;
  } catch { return null; }
}

async function incrementCounterApi() {
  try {
    const res = await fetch('https://api.counterapi.dev/v1/foley-demo-page/visits/up');
    if (res.ok) {
      const d = await res.json();
      (window as any).__visitCount = d.count ?? 0;
    }
  } catch { /* silent */ }
}

// ── Main entry ────────────────────────────────────────────────────────────────

export async function trackVisit() {
  const ua = navigator.userAgent;
  const { browser, version: browser_version } = parseBrowser(ua);

  const record: VisitorRecord = {
    id:               `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp:        new Date().toISOString(),
    ip: 'Fetching…',  country: '', country_code: '', region: '', city: '', timezone: '', isp: '',
    browser,          browser_version,
    os:               parseOS(ua),
    device:           parseDevice(ua),
    language:         navigator.language,
    referrer:         document.referrer || '(direct)',
    screen_resolution: `${screen.width}×${screen.height}`,
    viewport:         `${window.innerWidth}×${window.innerHeight}`,
    page_url:         location.href,
  };

  // 1. Save to localStorage immediately (placeholder geo)
  const local = loadLocalRecords();
  local.push(record);
  saveLocalRecords(local);
  (window as any).__foleyVisits = loadLocalRecords();

  // 2. Increment CounterAPI (fire-and-forget, even if Supabase is configured)
  incrementCounterApi();

  // 3. Resolve geo, then push final record everywhere
  try {
    console.log('%c[Foley DevTools] 正在获取 IP 地理信息...', 'color:#6366f1');
    const geo = await fetchGeo();
    Object.assign(record, geo);

    const updated = loadLocalRecords();
    const idx = updated.findIndex((r) => r.id === record.id);
    if (idx !== -1) updated[idx] = record;
    saveLocalRecords(updated);
    (window as any).__foleyVisits = loadLocalRecords();

    // Push complete record (with full geo) to Supabase
    pushToSupabase(record);

    console.log(
      `%c[Foley DevTools] ✓ IP: ${geo.ip} | 位置: ${[geo.city, geo.region, geo.country].filter(Boolean).join(', ')} | ${browser} / ${record.os} | ${record.device}${supabaseConfigured ? ' | ✓ Supabase已写入' : ''}`,
      'color:#059669;font-weight:bold'
    );
  } catch (err) {
    console.warn('[Foley DevTools] ✗ IP地理信息获取失败，将以空值写入:', err);
    record.ip = 'Unavailable';
    record.country = 'Unknown';
    pushToSupabase(record);
    const updated = loadLocalRecords();
    const idx = updated.findIndex((r) => r.id === record.id);
    if (idx !== -1) updated[idx] = record;
    saveLocalRecords(updated);
    (window as any).__foleyVisits = loadLocalRecords();
  }
}
